import React, { useState } from 'react';
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { Clock, TrendingUp, Zap, Users } from 'lucide-react';

const Metrics = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Updated data for more aggressive automation curve
  const data = [
    { month: 'Jan', withoutAutomation: 100, withAutomation: 100 },
    { month: 'Feb', withoutAutomation: 103, withAutomation: 140 },
    { month: 'Mar', withoutAutomation: 106, withAutomation: 200 },
    { month: 'Apr', withoutAutomation: 109, withAutomation: 280 },
    { month: 'May', withoutAutomation: 112, withAutomation: 380 },
    { month: 'Jun', withoutAutomation: 115, withAutomation: 500 },
  ];

  // Animation for numbers
  const AnimatedNumber = ({ n }: { n: number }) => {
    const { number } = useSpring({
      from: { number: 0 },
      number: inView ? n : 0,
      delay: 200,
      config: { mass: 1, tension: 20, friction: 10 }
    });

    return <animated.span>{number.to((n) => n.toFixed(0))}</animated.span>;
  };

  const metrics = [
    {
      icon: Clock,
      value: 5,
      label: language === "en" ? "Days Average Delivery" : "Jours en Moyenne pour la Livraison",
      suffix: language === "en" ? "days" : "jours"
    },
    {
      icon: TrendingUp,
      value: 300,
      label: language === "en" ? "Hours Saved Per Employee Yearly" : "Heures Économisées par Employé par An",
      suffix: language === "en" ? "hrs" : "h"
    },
    {
      icon: Zap,
      value: 70,
      label: language === "en" ? "Response Time Reduction" : "Réduction du Temps de Réponse",
      suffix: "%"
    },
    {
      icon: Users,
      value: 92,
      label: language === "en" ? "Client Satisfaction Rate" : "Taux de Satisfaction Client",
      suffix: "%"
    }
  ];

  return (
    <section 
      ref={ref}
      className="relative py-32 px-8 overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-32">
          <h2 className={`
            text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-4
            animate-gradient-x
          `}
          style={{ lineHeight: '1.2', paddingBottom: '10px' }}>
            {language === "en" ? "Impact Metrics" : "Métriques d'Impact"}
          </h2>
          <p className={`text-2xl md:text-3xl font-medium mb-8 ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}>
            {language === "en" 
              ? "Don't let your competition outpace you" 
              : "Ne laissez pas vos concurrents vous dépasser"}
          </p>
          <div className={`w-24 h-1 mx-auto rounded-full mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {metrics.map((metric, index) => (
            <div key={index} className={`
              p-6
              rounded-2xl
              border-2
              transition-all
              duration-300
              hover:-translate-y-1
              ${isDark 
                ? "bg-black/80 border-green-500/30 hover:border-green-400" 
                : "bg-white/80 border-green-900/30 hover:border-green-800"
              }
            `}>
              <div className="flex items-center gap-4 mb-4">
                <div className={`
                  w-12 h-12
                  rounded-xl
                  flex items-center justify-center
                  ${isDark 
                    ? "bg-green-500/10 border border-green-500/30" 
                    : "bg-green-900/10 border border-green-900/30"
                  }
                `}>
                  <metric.icon className={`w-6 h-6 ${
                    isDark ? "text-green-400" : "text-green-900"
                  }`} />
                </div>
                <div className={`text-3xl font-bold ${
                  isDark ? "text-green-400" : "text-green-900"
                }`}>
                  <AnimatedNumber n={metric.value} />
                  <span className="ml-1">{metric.suffix}</span>
                </div>
              </div>
              <p className={`text-lg ${
                isDark ? "text-gray-300" : "text-gray-700"
              }`}>
                {metric.label}
              </p>
            </div>
          ))}
        </div>

        {/* Revenue Chart */}
        <div className="flex flex-col lg:flex-row gap-8 items-center">
          {/* Chart */}
          <div className={`
            w-full
            p-8
            rounded-2xl
            border-2
            ${isDark 
              ? "bg-black/80 border-green-500/30" 
              : "bg-white/80 border-green-900/30"
            }
          `}>
            <h3 className={`text-2xl font-bold mb-8 ${
              isDark ? "text-green-400" : "text-green-900"
            }`}>
              {language === "en" 
                ? "Efficiency Impact on Revenue" 
                : "Impact de l'Efficacité sur les Revenus"}
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="withAutomation" x1="0" y1="0" x2="0" y2="1">
                      <stop 
                        offset="5%" 
                        stopColor={isDark ? "#22c55e" : "#14532d"} 
                        stopOpacity={0.8}
                      />
                      <stop 
                        offset="95%" 
                        stopColor={isDark ? "#22c55e" : "#14532d"} 
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient id="withoutAutomation" x1="0" y1="0" x2="0" y2="1">
                      <stop 
                        offset="5%" 
                        stopColor={isDark ? "#6b7280" : "#374151"} 
                        stopOpacity={0.8}
                      />
                      <stop 
                        offset="95%" 
                        stopColor={isDark ? "#6b7280" : "#374151"} 
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={isDark ? "#374151" : "#d1d5db"}
                  />
                  <XAxis 
                    dataKey="month" 
                    stroke={isDark ? "#d1d5db" : "#374151"}
                  />
                  <YAxis 
                    stroke={isDark ? "#d1d5db" : "#374151"}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: isDark ? '#111' : '#fff',
                      border: `1px solid ${isDark ? '#22c55e' : '#14532d'}`,
                      borderRadius: '0.5rem'
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{
                      paddingBottom: '20px',
                      color: isDark ? '#d1d5db' : '#374151'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="withoutAutomation"
                    stroke={isDark ? "#6b7280" : "#374151"}
                    fillOpacity={1}
                    fill="url(#withoutAutomation)"
                    name={language === "en" ? "Without Automation" : "Sans Automatisation"}
                  />
                  <Area
                    type="monotone"
                    dataKey="withAutomation"
                    stroke={isDark ? "#22c55e" : "#14532d"}
                    fillOpacity={1}
                    fill="url(#withAutomation)"
                    name={language === "en" ? "With Automation" : "Avec Automatisation"}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default Metrics; 